import type { ElementType, FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface RevealProps {
    as?: ElementType
    className?: string
    delay?: number
    children: ReactNode
    id?: string
}

/* Fades content up as it scrolls into view. Server HTML stays fully visible,
   and the shown state keeps no transform so overlays stack normally. */
export const Reveal: FC<RevealProps> = ({ as: Tag = 'div', className, delay = 0, children, id }): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const [state, setState] = useState<'idle' | 'hidden' | 'shown'>('idle')

    useEffect(() => {
        const element = ref.current

        if (!element || typeof IntersectionObserver === 'undefined') return

        const rect = element.getBoundingClientRect()

        if (rect.top < window.innerHeight * 0.92) return

        setState('hidden')

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setState('shown')
                    observer.disconnect()
                }
            })
        }, { rootMargin: '0px 0px -8% 0px' })

        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    return (
        <Tag
            ref={ref}
            id={id}
            className={cn(state === 'hidden' && 'reveal-hidden', state === 'shown' && 'reveal-shown', className)}
            style={state === 'shown' && delay ? { transitionDelay: `${delay}ms` } : undefined}
        >
            {children}
        </Tag>
    )
}
